import React, { createContext, useContext, useEffect, useState } from 'react';
import { getStoredToken, setStoredToken, clearStoredToken, requestFCMToken } from '../firebase';
import { trackInteraction } from '../api/interactions';
import { verifyMyCard, getMyCards } from '../api/card';
import { getMyMessages } from '../api/messages';

const API_BASE = process.env.REACT_APP_API_URL || 'https://epix-card-worker.rkvrh00.workers.dev';

// JWT 디코드 (검증 없이 payload만 파싱)
function decodeJWT(token) {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch {
    return null;
  }
}

// 토큰 만료 확인
function isTokenExpired(token) {
  const payload = decodeJWT(token);
  if (!payload?.exp) return true;
  return payload.exp < Math.floor(Date.now() / 1000);
}

// Workers API 호출용 idToken 반환 (Firebase getIdToken() 대체)
async function getIdToken() {
  const token = getStoredToken();
  if (!token || isTokenExpired(token)) return null;
  return token;
}

// Web Push 구독 등록
async function registerPushSubscription() {
  const sub = await requestFCMToken();
  if (!sub) return;

  const token = await getIdToken();
  if (!token) return;

  try {
    const subJson = sub.toJSON();
    await fetch(`${API_BASE}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        keys: { p256dh: subJson.keys?.p256dh, auth: subJson.keys?.auth },
      }),
    });
    trackInteraction(null, 'push_enabled', token);
  } catch {
    // 조용히 무시
  }
}

const AuthContext = createContext(null);

// user 객체 형태: { uid, email, displayName, photoURL, getIdToken }
// Firebase user 객체와 호환되는 형태 유지

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = 로딩 중
  const [cardStatus, setCardStatus] = useState('unknown');
  const [savedCard, setSavedCard] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // 저장된 토큰으로 user 복원
  useEffect(() => {
    const token = getStoredToken();
    if (token && !isTokenExpired(token)) {
      const payload = decodeJWT(token);
      const restoredUser = {
        uid: payload.sub,
        email: payload.email,
        displayName: payload.displayName ?? null,
        photoURL: payload.photoURL ?? null,
        getIdToken: async () => token,
      };
      setUser(restoredUser);
      registerPushSubscription();
      checkSavedCard(restoredUser);
      getMyMessages(token).then(msgs => {
        setUnreadCount(msgs.filter(m => !m.read).length);
      }).catch(() => {});
    } else {
      setUser(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkSavedCard = async (u) => {
    try {
      const token = await u.getIdToken();
      const localShortId = localStorage.getItem('myCardShortId');

      if (localShortId) {
        const result = await verifyMyCard(localShortId, token);
        if (result.status === 'ok') {
          setCardStatus('verified');
          setSavedCard(result);
          return;
        }
        localStorage.removeItem('myCardShortId');
      }

      const cards = await getMyCards(token);
      if (cards && cards.length > 0) {
        const latest = cards[0];
        localStorage.setItem('myCardShortId', latest.shortId);
        const result = await verifyMyCard(latest.shortId, token);
        if (result.status === 'ok') {
          setCardStatus('verified');
          setSavedCard(result);
          return;
        }
      }
      setCardStatus('no_card');
    } catch {
      setCardStatus('error');
    }
  };

  // 로그인 완료 후 user 세팅
  function applyLogin(token, profile) {
    const payload = decodeJWT(token);
    const newUser = {
      uid: payload.sub,
      email: payload.email,
      displayName: profile?.displayName ?? payload.displayName ?? null,
      photoURL: profile?.photoURL ?? payload.photoURL ?? null,
      getIdToken: async () => token,
    };
    setStoredToken(token);
    setUser(newUser);
    registerPushSubscription();
    checkSavedCard(newUser);
    getMyMessages(token).then(msgs => {
      setUnreadCount(msgs.filter(m => !m.read).length);
    }).catch(() => {});
    return newUser;
  }

  // ─── 이메일 로그인 ──────────────────────────────────────────────
  const loginWithEmail = async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || '로그인 실패');
    applyLogin(data.token, data);
    return { user: applyLogin(data.token, data) };
  };

  // ─── 이메일 회원가입 ────────────────────────────────────────────
  const signupWithEmail = async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || '회원가입 실패');
    applyLogin(data.token, data);
    return { user: applyLogin(data.token, data) };
  };

  // ─── Google 로그인 (Google Identity Services 팝업) ────────────
  const loginWithGoogle = async () => {
    return new Promise((resolve, reject) => {
      if (!window.google?.accounts?.id) {
        reject(new Error('Google SDK가 로드되지 않았습니다'));
        return;
      }
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
        callback: async (response) => {
          try {
            const res = await fetch(`${API_BASE}/api/auth/google`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ idToken: response.credential }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Google 로그인 실패');
            applyLogin(data.token, data);
            resolve({ user: applyLogin(data.token, data) });
          } catch (e) {
            reject(e);
          }
        },
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          reject(new Error('Google 로그인 팝업을 열 수 없습니다'));
        }
      });
    });
  };

  // ─── 로그아웃 ───────────────────────────────────────────────────
  const logout = () => {
    clearStoredToken();
    setUser(null);
    setCardStatus('unknown');
    setSavedCard(null);
    setUnreadCount(0);
  };

  const refreshCardStatus = (u) => checkSavedCard(u || user);

  const refreshUnreadCount = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const msgs = await getMyMessages(token);
      setUnreadCount(msgs.filter(m => !m.read).length);
    } catch {}
  };

  const isLoading = user === undefined;

  return (
    <AuthContext.Provider value={{
      user, isLoading,
      cardStatus, savedCard, refreshCardStatus,
      unreadCount, setUnreadCount, refreshUnreadCount,
      loginWithGoogle, loginWithEmail, signupWithEmail, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
